import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";
import * as XLSX from "xlsx-js-style";
import { canEdit, isSuperAdmin as isSuperAdminUser } from "../utils/permissions";

const EMPTY_FORM = {
  master_key_id: "",
  activated_date: "",
  duration_years: "",
  activated_end_date: "",
  master_key_password: "",
  remarks: "",
};

const YEAR_OPTIONS = [1, 2, 3, 4, 5];

const ROW_GROUP_COLORS = [
  "#BFDBFE",
  "#BBF7D0",
  "#FED7AA",
  "#DDD6FE",
  "#FBCFE8",
  "#A5F3FC",
];

const calcEndDate = (start, years) => {
  if (!start || !years) return "";
  return moment(start).add(Number(years), "years").format("YYYY-MM-DD");
};

/** Remaining days until Activated End Date (negative = expired). */
const daysDiff = (endDate) => {
  if (!endDate) return null;
  return moment(endDate).startOf("day").diff(moment().startOf("day"), "days");
};

const EXCEL_BORDER = {
  top: { style: "thin", color: { rgb: "D1D5DB" } },
  bottom: { style: "thin", color: { rgb: "D1D5DB" } },
  left: { style: "thin", color: { rgb: "D1D5DB" } },
  right: { style: "thin", color: { rgb: "D1D5DB" } },
};

export default function MsKeyList() {
  const { http, user } = AuthUser();
  const [keys, setKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDuration, setFilterDuration] = useState("");
  const [filterDays, setFilterDays] = useState("");
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const isSuperAdmin = isSuperAdminUser(user);
  const allowEdit = canEdit(user);

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = () => {
    setIsLoading(true);
    http
      .get("/ms_keys")
      .then((res) => {
        setIsLoading(false);
        if (res.data.success && Array.isArray(res.data.data)) {
          setKeys(res.data.data);
        } else {
          toast.error("Failed to fetch MS keys");
        }
      })
      .catch(() => {
        setIsLoading(false);
        toast.error("Error fetching MS keys");
      });
  };

  const formatDateInput = (value) => {
    if (!value) return "";
    return moment(value).format("YYYY-MM-DD");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "activated_date" || name === "duration_years") {
        next.activated_end_date = calcEndDate(
          name === "activated_date" ? value : next.activated_date,
          name === "duration_years" ? value : next.duration_years
        );
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allowEdit) {
      toast.error("View-only users cannot modify data");
      return;
    }
    if (!formData.master_key_id.trim()) {
      toast.error("Master Key Id is required");
      return;
    }
    setIsLoading(true);
    try {
      let response;
      if (editingKey) {
        response = await http.put(`/ms_keys/${editingKey.id}`, formData);
        toast.success("MS key updated successfully");
      } else {
        response = await http.post("/ms_keys", formData);
        toast.success("MS key created successfully");
      }
      if (response.data.success) {
        resetForm();
        fetchKeys();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving MS key");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditingKey(row);
    setFormData({
      master_key_id: row.master_key_id || "",
      activated_date: formatDateInput(row.activated_date),
      duration_years: row.duration_years ? String(row.duration_years) : "",
      activated_end_date: formatDateInput(row.activated_end_date),
      master_key_password: row.master_key_password || "",
      remarks: row.remarks || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this MS key?")) return;
    try {
      await http.delete(`/ms_keys/${id}`);
      toast.success("MS key deleted successfully");
      fetchKeys();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error deleting MS key");
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingKey(null);
    setShowModal(false);
  };

  const togglePassword = (id) => {
    setVisiblePasswords((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredKeys = keys.filter((row) => {
    const q = searchTerm.toLowerCase();
    if (q) {
      const hit =
        String(row.master_key_id || "").toLowerCase().includes(q) ||
        String(row.remarks || "").toLowerCase().includes(q) ||
        String(row.master_key_password || "").toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (filterDuration !== "" && Number(row.duration_years) !== Number(filterDuration)) {
      return false;
    }
    if (filterDays !== "") {
      const d = daysDiff(row.activated_end_date);
      if (d === null) return false;
      if (filterDays === "expired") return d < 0;
      return d < Number(filterDays);
    }
    return true;
  });

  const renderDaysDiff = (endDate) => {
    const d = daysDiff(endDate);
    if (d === null) return <span className="text-muted">—</span>;
    if (d < 0) {
      return (
        <span className="badge bg-danger" title="Expired">
          {d}
        </span>
      );
    }
    if (d < 30) {
      return (
        <span className="badge bg-warning text-dark" title="Expiring soon">
          {d}
        </span>
      );
    }
    return <span className="fw-bold text-success">{d}</span>;
  };

  const handleExcel = () => {
    if (!filteredKeys.length) {
      toast.error("No MS keys to export");
      return;
    }
    const headers = [
      "No",
      "Master Key Id",
      "Duration",
      "Activated Date",
      "Activated End Date",
      "Days Diff",
      "Master Key Password",
      "Remarks",
    ];
    const rows = filteredKeys.map((row, i) => [
      i + 1,
      row.master_key_id || "",
      row.duration_years ? `${row.duration_years} Year${row.duration_years > 1 ? "s" : ""}` : "",
      row.activated_date ? moment(row.activated_date).format("DD/MM/YYYY") : "",
      row.activated_end_date ? moment(row.activated_end_date).format("DD/MM/YYYY") : "",
      daysDiff(row.activated_end_date) ?? "",
      row.master_key_password || "",
      row.remarks || "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let r = range.s.r; r <= range.e.r; r += 1) {
      for (let c = range.s.c; c <= range.e.c; c += 1) {
        const ref = XLSX.utils.encode_cell({ r, c });
        if (!ws[ref]) continue;
        if (r === 0) {
          ws[ref].s = {
            fill: { fgColor: { rgb: "0D6EFD" } },
            font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
            alignment: { horizontal: "center", vertical: "center", wrapText: true },
            border: EXCEL_BORDER,
          };
        } else {
          ws[ref].s = {
            fill: { fgColor: { rgb: r % 2 === 0 ? "FFFFFF" : "F8FAFC" } },
            font: { sz: 10 },
            alignment: {
              horizontal: c === 0 || c === 5 ? "center" : "left",
              vertical: "center",
            },
            border: EXCEL_BORDER,
          };
        }
      }
    }
    ws["!cols"] = [
      { wch: 6 },
      { wch: 32 },
      { wch: 12 },
      { wch: 14 },
      { wch: 16 },
      { wch: 10 },
      { wch: 22 },
      { wch: 28 },
    ];
    ws["!rows"] = [{ hpt: 26 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MS Keys");
    XLSX.writeFile(wb, `ms_keys_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
  };

  return (
    <div className="container-fluid mt-3">
      <Toaster />
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="mb-0">MS Key</h4>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              disabled={!filteredKeys.length}
              onClick={handleExcel}
            >
              ⬇ Excel
            </button>
            {allowEdit && (
            <button className="btn btn-sm btn-primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus-circle me-1"></i> Add MS Key
            </button>
            )}
          </div>
        </div>
        <div className="card-body">
          <div className="row g-2 mb-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label mb-1" style={{ fontSize: "12px" }}>
                Search
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by key, password or remarks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1" style={{ fontSize: "12px" }}>
                Duration
              </label>
              <select
                className="form-select form-select-sm"
                value={filterDuration}
                onChange={(e) => setFilterDuration(e.target.value)}
              >
                <option value="">All Duration</option>
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>
                    {y} Year{y > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label mb-1" style={{ fontSize: "12px" }}>
                Days Diff
              </label>
              <select
                className="form-select form-select-sm"
                value={filterDays}
                onChange={(e) => setFilterDays(e.target.value)}
              >
                <option value="">All Days</option>
                <option value="30">Less than 30 Days</option>
                <option value="60">Less than 60 Days</option>
                <option value="90">Less than 90 Days</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  setSearchTerm("");
                  setFilterDuration("");
                  setFilterDays("");
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {isLoading && keys.length === 0 ? (
            <TaskSkeleton />
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered align-middle ms-master-table">
                <thead className="table-primary">
                  <tr>
                    <th>No</th>
                    <th>Master Key Id</th>
                    <th>Duration</th>
                    <th>Activated Date</th>
                    <th>Activated End Date</th>
                    <th>Days Diff</th>
                    <th>Master Key Password</th>
                    <th>Remarks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKeys.length > 0 ? (
                    filteredKeys.map((row, index) => (
                      <tr
                        key={row.id}
                        className="group-row"
                        style={{
                          "--row-bg":
                            ROW_GROUP_COLORS[index % ROW_GROUP_COLORS.length],
                        }}
                      >
                        <td>{index + 1}</td>
                        <td className="fw-bold">{row.master_key_id || "—"}</td>
                        <td>
                          {row.duration_years
                            ? `${row.duration_years} Year${row.duration_years > 1 ? "s" : ""}`
                            : "—"}
                        </td>
                        <td>
                          {row.activated_date
                            ? moment(row.activated_date).format("DD/MM/YYYY")
                            : "—"}
                        </td>
                        <td>
                          {row.activated_end_date
                            ? moment(row.activated_end_date).format("DD/MM/YYYY")
                            : "—"}
                        </td>
                        <td className="text-center">{renderDaysDiff(row.activated_end_date)}</td>
                        <td>
                          {row.master_key_password ? (
                            <span className="d-inline-flex align-items-center gap-2">
                              <span>
                                {visiblePasswords[row.id]
                                  ? row.master_key_password
                                  : "••••••••"}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                style={{ padding: "2px 6px" }}
                                onClick={() => togglePassword(row.id)}
                                title={visiblePasswords[row.id] ? "Hide" : "Show"}
                              >
                                <i className={`fas ${visiblePasswords[row.id] ? "fa-eye-slash" : "fa-eye"}`}></i>
                              </button>
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td style={{ maxWidth: "240px" }} title={row.remarks || ""}>
                          {row.remarks || "—"}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {allowEdit && (
                          <button
                            className="btn btn-sm btn-warning me-1"
                            onClick={() => handleEdit(row)}
                            title="Edit"
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                          )}
                          {allowEdit && isSuperAdmin && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(row.id)}
                              title="Delete"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          )}
                          {!allowEdit && <span className="text-muted">View only</span>}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center">
                        No MS keys found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingKey ? "Edit MS Key" : "Add MS Key"}</h5>
                <button type="button" className="btn-close" onClick={resetForm}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Master Key Id <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="master_key_id"
                        value={formData.master_key_id}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Master Key Password</label>
                      <input
                        type="text"
                        className="form-control"
                        name="master_key_password"
                        value={formData.master_key_password}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">MS Key Activated Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="activated_date"
                        value={formData.activated_date}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Duration</label>
                      <select
                        className="form-select"
                        name="duration_years"
                        value={formData.duration_years}
                        onChange={handleInputChange}
                      >
                        <option value="">Select years</option>
                        {YEAR_OPTIONS.map((y) => (
                          <option key={y} value={y}>
                            {y} Year{y > 1 ? "s" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">MS Key Activated End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="activated_end_date"
                        value={formData.activated_end_date}
                        onChange={handleInputChange}
                        readOnly={!!formData.duration_years}
                      />
                    </div>
                    <div className="col-md-12 mb-0">
                      <label className="form-label">Remarks</label>
                      <textarea
                        className="form-control"
                        name="remarks"
                        rows={3}
                        value={formData.remarks}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading ? "Saving..." : editingKey ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .ms-master-table tbody tr.group-row > td {
          background-color: var(--row-bg) !important;
        }
        .ms-master-table tbody tr.group-row:hover > td {
          filter: brightness(0.97);
        }
      `}</style>
    </div>
  );
}
